import { Box, type SystemStyleObject } from "@chakra-ui/react";
import { InputActionMeta, Select, useChakraSelectProps } from "chakra-react-select";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { SelectorOptions, SelectorValue } from "./SelectorValue";
import { useSelectScreenReaderMessages } from "./useSelectScreenReaderMessages";

interface Props {
  value: SelectorValue[];
  options: SelectorOptions;
  noOptionsAvailableMessage: string;
  onChange: (values: SelectorValue[]) => void;
  inputValue?: string;
  onInputChange?: ((newValue: string, actionMeta: InputActionMeta) => void) | undefined;
  isLoading?: boolean;
  placeholder?: {
    text: string;
    color: string;
  };
  searchValues?: boolean;
  size?: "sm" | "md";
}

export const MultiSelector = ({
  value,
  options,
  noOptionsAvailableMessage,
  onChange,
  inputValue,
  onInputChange,
  isLoading,
  placeholder,
  searchValues = true,
  size = "md",
}: Props) => {
  const [selectedValues, setSelectedValues] = useState<SelectorValue[]>([]);
  const { t } = useTranslation();
  const selectScreenReaderMessages = useSelectScreenReaderMessages<SelectorValue, true>();

  useEffect(() => {
    setSelectedValues(value);
  }, [value]);

  const getNoOptionsMessage = () => {
    if (options.length === 0) return <Box>{noOptionsAvailableMessage}</Box>;
    else return <Box>{t("No values found")}</Box>;
  };

  return (
    <Select<SelectorValue>
      {...selectScreenReaderMessages}
      isLoading={isLoading}
      {...useChakraSelectProps<SelectorValue>({
        isMulti: true,
        size,
        value: selectedValues,
        onChange: (selectedOptions: SelectorValue[]) => {
          const values = selectedOptions.map((option: SelectorValue) => ({
            value: option.value,
            label: option.label,
          }));
          setSelectedValues(values);
          onChange(values);
        },
        isClearable: false,
        selectedOptionStyle: "check",
        selectedOptionColor: "blue",
        placeholder: placeholder ? <Box color={placeholder.color}>{placeholder.text}</Box> : <Box></Box>,
        noOptionsMessage: getNoOptionsMessage,
      })}
      inputValue={inputValue}
      onInputChange={onInputChange}
      filterOption={customFilterOption({ disableFrontendFiltering: !!onInputChange, searchLabelsOnly: !searchValues })}
      options={options}
      chakraStyles={{
        control: (provided: SystemStyleObject) => ({
          ...provided,
          backgroundColor: "smBackground",
          rounded: "md",
        }),
        container: (provided: SystemStyleObject) => ({
          ...provided,
          width: "full",
        }),
        menuList: (provided: SystemStyleObject) => ({
          ...provided,
          rounded: "md",
        }),
      }}
      menuPortalTarget={document.body}
      menuPosition="fixed"
      styles={{ menuPortal: (base: SystemStyleObject) => ({ ...base, zIndex: 9999 }) }}
    />
  );
};

const customFilterOption = ({
  disableFrontendFiltering,
  searchLabelsOnly,
}: {
  disableFrontendFiltering: boolean;
  searchLabelsOnly: boolean;
}) => {
  if (disableFrontendFiltering) return () => true;
  if (searchLabelsOnly) return filterLabels;
  return undefined;
};

const filterLabels = (option: SelectorValue, inputValue: string) => {
  return option.label.toLowerCase().includes(inputValue.toLowerCase());
};
